/**
 * Отправка позиций логисту на нетарифку (с 17.10.2026) и отчёт «Нетарифка»
 * в «Аналитике»: какие позиции менеджеры отправляли, кто и когда.
 * Паспорт — docs/features/nontariff.md.
 */
import { z } from 'zod'
import type { CustomsCheckStatus } from '../constants/customs'

const uuid = z.string().uuid()
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Дата в формате ГГГГ-ММ-ДД')

export const CustomsSendToLogistSchema = z.object({
  itemIds: z.array(uuid).min(1).max(2000),
})
export type CustomsSendToLogistDto = z.infer<typeof CustomsSendToLogistSchema>

export interface CustomsSendToLogistResult {
  /** Ушли логисту сейчас. */
  sent: number
  /** Уже были у логиста — повторно не отправлены. */
  alreadySent: number
  /** Не из-за рубежа (РФ, Склад, без матрицы), отменены или заявка не в работе. */
  skipped: number
}

/** Период — как у остальных вкладок «Аналитики»: month/quarter/year/custom. */
export const CustomsSendReportQuerySchema = z.object({
  period: z.enum(['month', 'quarter', 'year', 'custom']).optional(),
  date: z.string().max(10).optional(),
  from: isoDate.optional(),
  to: isoDate.optional(),
  /** Поиск по партномеру: без периода, по всем заявкам — «чья это позиция». */
  search: z.string().trim().max(100).optional(),
})
export type CustomsSendReportQuery = z.infer<typeof CustomsSendReportQuerySchema>

/** Поставить или снять «Нужна была отправка» у неотправленных позиций. */
export const CustomsMissedMarkSchema = z.object({
  itemIds: z.array(z.string().uuid()).min(1, 'Не выбраны позиции').max(500),
  missed: z.boolean(),
})
export type CustomsMissedMarkDto = z.infer<typeof CustomsMissedMarkSchema>

export interface CustomsMissedMarkResult {
  changed: number
  /** Пропущены: позицию уже отправляли логисту или она не из-за рубежа. */
  skipped: number
}

export interface CustomsSendReportItem {
  itemId: string
  partNumber: string
  matrix: string | null
  sent: boolean
  /** Последняя отправка логисту. */
  sentAt: string | null
  sentByName: string | null
  /** Статус нетарифки сейчас (null — позицию не отправляли). */
  status: CustomsCheckStatus | null
  attention: boolean
  /** «Нужна была отправка»: кто и когда отметил (null — не отмечено). */
  missed: { at: string; byName: string | null } | null
  /** Партномер совпал с поиском (только при поиске). */
  match?: boolean
}

export interface CustomsSendReportRequest {
  requestId: string
  requestNumber: string | null
  clientName: string | null
  createdAt: string
  managerId: string
  managerName: string | null
  total: number
  sent: number
  lastSentAt: string | null
  attentionCount: number
  missedCount: number
  items: CustomsSendReportItem[]
}

export interface CustomsSendReportManager {
  managerId: string
  managerName: string | null
  requests: number
  total: number
  sent: number
  notSent: number
  missed: number
}

export interface CustomsSendReport {
  /**
   * С какого дня считаем (день первой отправки логисту): раньше позиции падали
   * логисту сами, «не отправлено» до этого дня — не ошибка менеджера. null — отправок ещё не было.
   */
  since: string | null
  totals: {
    total: number
    sent: number
    notSent: number
    requestsWithUnsent: number
    missed: number
  }
  managers: CustomsSendReportManager[]
  requests: CustomsSendReportRequest[]
}
